import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { env } from '@/scripts/env';
import type { usp_EnrollmentInfo_Result } from '@sas/db/procedures';
import type { dbo, pas } from '@sas/shared/database';
import type { AdminFiles, PolicyFiles } from '@sas/shared/documents';
import { enrollmentBundleSchema, referenceBundleSchema } from '@sas/shared/schemas/reference';

const REFERENCE_URL = `${env.PUBLIC_REFERENCE_DATA_URL}/reference.json`;
const ENROLLMENT_URL = (pk: number) => `${env.PUBLIC_REFERENCE_DATA_URL}/enrollment/${pk}.json`;
const STORAGE_REFERENCE = 'sas:reference';
const STORAGE_ENROLLMENT = (pk: number) => `sas:enrollment:${pk}`;

/**
 * Reference + enrollment blobs are written by the admin-api timer with `/documents/{path}`
 * (root-relative) URLs baked in. In prod the SPA fetches from the blob storage origin, and SWA
 * route rewrites can't proxy absolute URLs — so the SPA rewrites stored `/documents/...` references
 * to `${PUBLIC_DOCUMENTS_URL}/...` at fetch time.
 */
function absolutizeDocumentUrls<T>(data: T): T {
  const base = env.PUBLIC_DOCUMENTS_URL;
  return JSON.parse(JSON.stringify(data).replaceAll('"/documents/', `"${base}/`)) as T;
}

/**
 * Single bundle fetched once per session and mirrored to localStorage. Drives every
 * state/school/document list on the public site without requiring a round-trip for each page.
 */
export interface ReferenceData {
  generatedAt: string;
  year: number;
  states: dbo.State[];
  schools: pas.School[];
  schoolFiles: Record<number, PolicyFiles>;
  /**
   * Per-school admin document URLs (master/voluntary/catastrophic/guaranty PDFs). Written by the
   * admin-api timer, read server-side by the public-api admin-login handler — never consumed by the
   * public UI directly. Present on the interface so the blob shape stays coherent across
   * writer/reader.
   */
  adminFiles?: Record<number, AdminFiles>;
}

/**
 * Per-school enrollment payload — keeps the rate matrix out of the global reference bundle so the
 * initial hydration stays small.
 */
export type EnrollmentData = usp_EnrollmentInfo_Result & { generatedAt: string };

function hasStorage(): boolean {
  return typeof window !== 'undefined' && window.localStorage !== undefined;
}

function readStorage<T>(key: string): T | null {
  if (!hasStorage()) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: unknown): void {
  if (!hasStorage()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota exceeded or storage disabled — non-fatal, cache skips this write.
  }
}

/**
 * Reactive, localStorage-backed cache for every read-only payload the public UI consumes. Pages
 * read `store.*` getters synchronously; the store fires async refreshes and Vue reactivity
 * propagates updates.
 */
// eslint-disable-next-line max-lines-per-function -- [Project-convention] Pinia setup stores aggregate state + computed + actions in one factory by design.
export const useReferenceStore = defineStore('reference', () => {
  /// Data
  // Deliberately NOT seeded from localStorage here — see hydrateFromCache(). Reading the cache at
  // construction is what made a returning visitor's first client render disagree with the
  // prerendered HTML.
  const reference = shallowRef<ReferenceData | null>(null);
  const enrollments = ref<Map<number, EnrollmentData>>(new Map());
  let refreshing: Promise<ReferenceData | null> | null = null;
  const enrollmentRefreshes = new Map<number, Promise<EnrollmentData | null>>();

  /// Computed
  const states = computed<dbo.State[]>(() => reference.value?.states ?? []);
  const schools = computed<pas.School[]>(() => reference.value?.schools ?? []);
  const year = computed<number | null>(() => reference.value?.year ?? null);

  /// Methods

  /**
   * Seeds `reference` from the localStorage cache. **Must run after the app has hydrated** —
   * `App.vue` calls it from `onMounted`, which is the whole point of it being a method rather than
   * the ref's initial value.
   *
   * Public-ui is pre-rendered, and the pre-render has no localStorage, so every page ships HTML
   * built from an empty bundle. If the store read the cache at construction, a returning visitor's
   * first client render would produce a populated `<PolicyLookup>` against that empty markup and
   * Vue would report a hydration mismatch on `/k12` and `/college` — once per full page load, for
   * the life of the cache. Reading it after hydration makes the initial render match by
   * construction and turns the cache into an ordinary reactive update.
   *
   * No-ops when `refresh()` already won the race, so a slow cache read can never clobber fresh
   * data.
   */
  function hydrateFromCache(): void {
    if (reference.value) return;
    const cached = readStorage<ReferenceData>(STORAGE_REFERENCE);
    if (cached) reference.value = cached;
  }

  /**
   * Filters `states` to those with at least one school of the given type, sorted A–Z by name.
   * Reactive — re-computes automatically when `reference` updates.
   *
   * @param type - School type code (e.g. `'College'`, `'K12'`).
   * @returns States with at least one school of `type`; empty when the bundle hasn't loaded.
   */
  function statesFor(type: string): dbo.State[] {
    const data = reference.value;
    if (!data) return [];
    const codes = new Set<string>();
    for (const school of data.schools) {
      if (school.SCH_Type === type && school.STA_Code) codes.add(school.STA_Code);
    }
    return data.states
      .filter((s) => codes.has(s.STA_Code))
      .toSorted((a, b) => a.STA_Name.localeCompare(b.STA_Name));
  }

  /**
   * Filters `schools` by type + state code, sorted A–Z by display name.
   *
   * @param type - School type code.
   * @param staCode - Two-letter state code; empty/undefined returns an empty list.
   * @returns Matching schools, or empty when the bundle hasn't loaded.
   */
  function schoolsFor(type: string, staCode: string | undefined): pas.School[] {
    if (!staCode || !reference.value) return [];
    return reference.value.schools
      .filter((s) => s.SCH_Type === type && s.STA_Code === staCode)
      .toSorted((a, b) => (a.SCH_DisplayName ?? '').localeCompare(b.SCH_DisplayName ?? ''));
  }

  /**
   * Returns the current/previous-year document bundle for a school.
   *
   * @param schoolPk - School primary key; nullish values short-circuit to `null`.
   * @returns `PolicyFiles` for the school, or `null` when the bundle is absent.
   */
  function filesFor(schoolPk: number | null | undefined): PolicyFiles | null {
    if (schoolPk == null) return null;
    return reference.value?.schoolFiles?.[schoolPk] ?? null;
  }

  /**
   * Returns cached enrollment data for a school, synchronously hydrating from localStorage on first
   * access. Null if nothing is cached yet.
   *
   * @param schoolPk - School primary key.
   * @returns Cached `EnrollmentData`, or `null` when no cache entry exists.
   */
  function enrollmentFor(schoolPk: number): EnrollmentData | null {
    const existing = enrollments.value.get(schoolPk);
    if (existing) return existing;
    const cached = readStorage<EnrollmentData>(STORAGE_ENROLLMENT(schoolPk));
    if (cached) enrollments.value.set(schoolPk, cached);
    return cached;
  }

  /**
   * Fetches the reference bundle and mirrors it to localStorage. Deduplicates concurrent calls.
   *
   * @returns The fresh bundle on success, the stale in-memory bundle on network error, or `null`
   *   when nothing was previously cached.
   */
  function refresh(): Promise<ReferenceData | null> {
    if (refreshing) return refreshing;
    refreshing = (async () => {
      try {
        const response = await fetch(REFERENCE_URL, { method: 'GET' });
        if (!response.ok) return reference.value;
        const parsed = referenceBundleSchema.safeParse(await response.json());
        if (!parsed.success) {
          console.error('reference.json failed schema validation:', parsed.error.issues);
          return reference.value;
        }
        const data = absolutizeDocumentUrls(parsed.data as ReferenceData);
        reference.value = data;
        writeStorage(STORAGE_REFERENCE, data);
        return data;
      } catch (err) {
        console.error('Failed to refresh reference data:', err);
        return reference.value;
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  }

  /**
   * Client bootstrap for the reference bundle: seed from the cache, then start the network refresh.
   * `App.vue` calls this from `onMounted` and nothing else should call it.
   *
   * **It exists to make the ordering unmissable.** Both halves must run _after_ the app has
   * hydrated, and they used to be two calls in two different files — `hydrateFromCache()` in
   * `App.vue`, `refresh()` in `main.ts`'s setup callback. That callback runs before `app.mount()`
   * (qssg awaits `router.isReady()` in between), so a fetch resolving in the gap populated the
   * store ahead of hydration and the first client render disagreed with the pre-rendered HTML.
   * `DropDownList` renders its `<ListboxOptions :unmount="false">` children even while closed, so
   * that is one node per state and school on `/k12` and `/college`.
   *
   * Cache first, then network: `hydrateFromCache()` no-ops once `refresh()` has landed, so the
   * order is what gives a returning visitor instant content without risking stale data overwriting
   * fresh.
   */
  function initAfterHydration(): void {
    hydrateFromCache();
    void refresh();
  }

  /**
   * Fetches the enrollment payload for a school and mirrors it to localStorage. Returns null on 404
   * so callers can route to /error/404.
   *
   * @param schoolPk - School primary key.
   * @returns The fresh payload on 200, `null` on 404, or the stale cached value on other errors.
   */
  function refreshEnrollment(schoolPk: number): Promise<EnrollmentData | null> {
    const inflight = enrollmentRefreshes.get(schoolPk);
    if (inflight) return inflight;
    const promise = (async () => {
      try {
        const response = await fetch(ENROLLMENT_URL(schoolPk), { method: 'GET' });
        if (response.status === 404) return null;
        if (!response.ok) return enrollments.value.get(schoolPk) ?? null;
        const parsed = enrollmentBundleSchema.safeParse(await response.json());
        if (!parsed.success) {
          console.error(
            `enrollment/${schoolPk}.json failed schema validation:`,
            parsed.error.issues,
          );
          return enrollments.value.get(schoolPk) ?? null;
        }
        const data = absolutizeDocumentUrls(parsed.data as unknown as EnrollmentData);
        enrollments.value.set(schoolPk, data);
        writeStorage(STORAGE_ENROLLMENT(schoolPk), data);
        return data;
      } catch (err) {
        console.error(`Failed to refresh enrollment ${schoolPk}:`, err);
        return enrollments.value.get(schoolPk) ?? null;
      } finally {
        enrollmentRefreshes.delete(schoolPk);
      }
    })();
    enrollmentRefreshes.set(schoolPk, promise);
    return promise;
  }

  return {
    reference,
    enrollments,
    states,
    schools,
    year,
    hydrateFromCache,
    initAfterHydration,
    statesFor,
    schoolsFor,
    filesFor,
    enrollmentFor,
    refresh,
    refreshEnrollment,
  };
});
